import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for animating numbers from 0 to target value
 * @param {number} targetValue - The final value to animate to
 * @param {number} duration - Animation duration in milliseconds (default: 1000)
 * @param {boolean} shouldAnimate - Whether to animate or show value immediately
 * @returns {number} - The current animated value
 */
const useAnimatedCounter = (targetValue, duration = 1000, shouldAnimate = true) => {
  const [currentValue, setCurrentValue] = useState(0);
  const startTimeRef = useRef(null);
  const animationFrameRef = useRef(null);
  const previousTargetRef = useRef(targetValue);

  useEffect(() => {
    if (!shouldAnimate) {
      setCurrentValue(targetValue);
      return;
    }

    // If target changed, start new animation from current value
    const startValue = previousTargetRef.current !== targetValue ? currentValue : 0;
    previousTargetRef.current = targetValue;

    const animate = (timestamp) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function: easeOutExpo for smooth deceleration
      const easeOutExpo = 1 - Math.pow(2, -10 * progress);
      
      const newValue = Math.round(startValue + (targetValue - startValue) * easeOutExpo);
      setCurrentValue(newValue);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    startTimeRef.current = null;
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [targetValue, duration, shouldAnimate]);

  return currentValue;
};

export default useAnimatedCounter;
